import { breakpoints, Heading, Inline, Stack, Text, vars } from '@etg/wings';
import { BrandLogo } from '@eti/components';
import { useTranslation } from '@eti/providers';
import type { Dispatch, SetStateAction } from 'react';
import { constants } from '@eti/utils';
import { css, cx } from '@eti/styles';
import LoginMessage from '../containers/LoginMessage';
import LoginFormContainer from '../containers/LoginFormContainer';
import { SourceOfLogin } from '../containers/models';
import TitledCard from '../../components/TitledCard';
import NumberedSteps from '../../../pages/order-details/components/common/NumberedSteps';
import { cardStyles, flexContentWrapperStyles } from './styles';

const { GOTOGATE, MYTRIP, FLIGHTNETWORK, SEAT24, TRAVELSTART, FLYBILLET, SUPERSAVER } =
  constants.brands;

const brandLogos = [
  { name: MYTRIP, shouldUseContrast: false },
  { name: GOTOGATE, shouldUseContrast: false },
  { name: FLIGHTNETWORK, shouldUseContrast: true },
  { name: SEAT24, shouldUseContrast: true },
  { name: TRAVELSTART, shouldUseContrast: true },
  { name: FLYBILLET, shouldUseContrast: true },
  { name: SUPERSAVER, shouldUseContrast: true },
];

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  width: 100%;
`;

const logoWrapperStyles = css`
  padding: 16px 0 0;

  @media (min-width: ${breakpoints._768}) {
    width: 568px;
  }
`;

const logoStyles = css`
  display: flex;
  max-width: 90px;

  @media (min-width: ${breakpoints._768}) {
    height: 32px;
    max-width: 125px;
  }
`;

const marginStyles = css`
  margin-top: 16px;
`;

interface RyanairLoginProps {
  notification: { message: string; type: string; dataTestId: string } | null;
  setNotification: Dispatch<
    SetStateAction<{ message: string; type: string; dataTestId: string } | null>
  >;
}

const RyanairLogin = ({ notification, setNotification }: RyanairLoginProps) => {
  const { t } = useTranslation();
  const ryanairRequestRefundSteps = [...Array(3)].map((_, index) => ({
    text: t(`Postbooking.Login.RyanairFlight.RefundRequest.Step.${index + 1}`),
  }));

  return (
    <>
      <div className={flexContentWrapperStyles} data-testid="ryanair-login-page">
        <Stack>
          <TitledCard
            className={cx(marginStyles, cardStyles)}
            headingLevel={1}
            title={t('Postbooking.Login.RyanairFlight.Title')}
          >
            <Text data-testid="ryanair-login-page-subtitle">
              {t('Postbooking.Login.RyanairFlight.Subtitle')}
            </Text>
            <Inline align="start" className={logoWrapperStyles}>
              {brandLogos.map(({ name, shouldUseContrast }) => (
                <BrandLogo
                  brand={name}
                  className={logoStyles}
                  data-testid={`${name}-logo`}
                  key={`${name}-logo`}
                  shouldUseContrast={shouldUseContrast}
                />
              ))}
            </Inline>
          </TitledCard>
          <TitledCard
            className={cardStyles}
            data-testid="rayanair-login-form-card"
            headingAs="h2"
            headingLevel={3}
            title={t('Postbooking.Login.RyanairFlight.RefundRequest.Steps.Title')}
          >
            <Stack spacing={24}>
              <Stack>
                <NumberedSteps
                  dataTestId="ryanair-refund-login"
                  steps={ryanairRequestRefundSteps}
                />
                <Text asHtml>{t('Postbooking.Login.RyanairFlight.RefundRequest.Disclaimer')}</Text>
              </Stack>
              <span className={dividerStyles} />
              {notification?.message && notification?.type && (
                <LoginMessage notification={notification} />
              )}
              <Heading as="h3" level={4}>
                {t('Postbooking.Login.RyanairFlight.Form.Title')}
              </Heading>
              <LoginFormContainer
                setNotification={setNotification}
                source={SourceOfLogin.RYANAIR}
              />
            </Stack>
          </TitledCard>
        </Stack>
      </div>
    </>
  );
};

export default RyanairLogin;
